import classnames from 'classnames';
import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Loading,
  MessagePlugin,
  Select,
  SelectValue,
  Textarea,
  TextareaValue,
  Tooltip,
} from 'tdesign-react';
import FormItem from 'tdesign-react/es/form/FormItem';

import { AddBotAudio, AddBotLightAudio, BotIcon, EventPauseDub, EventPlayDub, SystemIcon } from '@/components/icon';
import { ContentType, EventType, IEventVoiceList, MissionType, PLAY_INFO_ID, RELATEDMESSAGE, SenderType } from '@/const/mission';
import { IEventMessageText, IEvents } from '@/interface';
import { AiVoiceReq } from '@/interface/aigc';
import { EAsyncStatus, RichMediaValue } from '@/interface/media';
import UploadAudio from '@/material/form-upload/audio';
import { AudioPlayer } from '@/material/form-upload/core/player/audio/audio';
import objectReducer, { reducerActions } from '@/reducer/objectReducers';
import { aiVoice } from '@/services/aigc';
import { CgiErrorCode } from '@/services/cgi';

import styles from '../index.module.less';

const SYSTEM_ID = 'sender_system';
const ignoreBot = ['relatedMessages'];
const NO_VOICE_INFO = '0';

interface IProps {
  contentEventIdColWidth: number | undefined;
  index: number;
  fieldname: string;
  value: IEvents;
  disabled: boolean;
  showSystem: boolean, // 是否显示旁白
  missionType?: MissionType
  botList: { name: string, id: string, url: string }[];
  voiceList: IEventVoiceList[];
  onChange: (value: any) => void;
  onEnter: (value: any) => void;
}

const BotItem: React.FC<IProps> = (props) => {
  const { value, onChange, disabled, showSystem, botList, voiceList, onEnter, index, fieldname, missionType, contentEventIdColWidth } = props;
  const textareaRef = useRef<any>(null);
  const onCompositionStartCaptureRef = useRef<any>(null);
  const changed = useRef(false);

  const asyncRef = useRef(false); // 异步任务

  const [lock, setLock] = useState(false);
  const [focus, setFocus] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);

  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [lastWord, setLastWord] = useState('');
  const audioRef = useRef<any>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const getStatus = useCallback((status: boolean) => {
    setIsPlaying(status);
  }, []);

  const [objectData, dispatch] = useReducer(objectReducer, {
    params: {
    },
    changeNumber: 0,
  });

  useEffect(
    () => {
      setOpenUpload(value?.message?.text?.openWebDub
        || openUpload
        || value?.message?.text?.asyncLoading?.asyncStatus === EAsyncStatus.UPLOAD_LOADING);
    },
    [
      value?.message?.text?.openWebDub,
      value?.message?.text?.asyncLoading?.asyncStatus,
    ],
  );

  useEffect(() => {
    if (!isEmpty(value) && isEmpty(objectData?.params)) {
      const sender = getSender(value);
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: {
          text: value?.message?.text?.text || '',
          sender: {
            type: sender?.senderType,
            senderId: sender?.senderId,
          },
          dubUrl: value?.message?.text?.dubUrl,
          asyncLoading: value?.message?.text?.asyncLoading,
          openWebDub: value?.message?.text?.openWebDub || getOpenWebDub(value),
          volume: value?.message?.text?.volume,
        },
      });
    }
  }, [
    value?.message?.text?.text,
    value?.message?.sender?.type,
    value?.message?.sender?.senderId,
    value?.message?.text?.dubUrl,
    value?.message?.text?.volume,
  ]);

  useEffect(() => {
    asyncRef.current = false;
    return () => {
      asyncRef.current = true;
    };
  }, []);

  const getOpenWebDub = (value: IEvents) => {
    if (!isEmpty(value?.message?.text?.dubUrl)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (value?.message?.text?.dubUrl?.url) {
      setOpenUpload(true);
    }
  }, [value?.message?.text?.dubUrl?.url]);

  useEffect(() => {
    if (objectData?.changeNumber && !isEmpty(objectData?.params) && changed?.current) {
      const postData: IEvents = {
        type: EventType.EVENT_TYPE_MESSAGE,
        message: {
          type: ContentType.CONTENT_TYPE_TEXT,
          ...value?.message,
          text: {
            showType: 0,
            ...value?.message?.text,
            text: objectData?.params?.text || '',
            dubUrl: objectData?.params?.dubUrl,
            openWebDub: objectData?.params?.openWebDub,
            asyncLoading: objectData?.params?.asyncLoading,
            volume: objectData?.params?.volume,
          },
          sender: objectData?.params?.sender,
        },
      };
      if (onCompositionStartCaptureRef.current) {
        return;
      }
      onChange(postData);
    }
  }, [objectData?.changeNumber]);

  const handlePause = () => {
    try {
      audioRef?.current?.triggerPlay();
    } catch (error) {
      MessagePlugin.error('该音频不支持播放');
    }
  };

  const findVoice = useCallback(() => {
    const senderId = value?.message?.sender?.senderId;
    const senderType = value?.message?.sender?.type;
    let findVoice: IEventVoiceList | undefined = undefined;
    if (senderType === SenderType.BOT) {
      findVoice = voiceList.find(item => item?.botId === senderId);
    }
    if (senderType === SenderType.SYSTEM) {
      findVoice = voiceList.find(item => item?.type === SenderType.SYSTEM);
    }
    return findVoice;
  }, [voiceList, value]);

  const handlePlay = async () => {
    if (value?.message?.sender?.type === SenderType.USER) {
      MessagePlugin.error('玩家不支持AI配音，请体验其他角色AI配音效果～');
      return;
    }
    if (!(text?.trim())) {
      MessagePlugin.error('请输入内容后试听');
      return;
    }

    const voice = findVoice();
    if (!voice?.voice?.voiceId || voice?.voice?.voiceId === NO_VOICE_INFO) {
      MessagePlugin.error('未配置语音包，无法试听，旁白在故事配置中选择，BOT角色请去角色配置中选择');
      return;
    }

    if (text === lastWord && audioUrl) {
      audioRef.current?.triggerPlay();
      return;
    }
    setLoading(true);
    const postData: AiVoiceReq = {
      voiceInfo: voice?.voice,
      text,
      senderType: value?.message?.sender?.type,
    };
    const { code, data: resData, message } = await aiVoice(postData);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || '试听失败，请点击重试',
      });
      setLoading(false);
      return;
    }

    setAudioUrl(resData);
    setLastWord(text);
    setTimeout(() => {
      setLoading(false);
      audioRef?.current?.triggerPlay();
    }, 500);
  };

  const getSender = (value: IEvents) => {
    if (isSystem) {
      return {
        senderType: SenderType.SYSTEM,
        senderId: '',
      };
    }
    if (value?.message?.sender?.senderId === PLAY_INFO_ID) {
      return {
        senderType: SenderType.USER,
        senderId: PLAY_INFO_ID,
      };
    }
    return {
      senderType: SenderType.BOT,
      senderId: value?.message?.sender?.senderId || '',
    };
  };

  const isSystem = useMemo(
    () => value?.message?.sender?.type === SenderType.SYSTEM
    , [value?.message?.sender?.type],
  );

  const handleStart = useCallback(() => {
    onCompositionStartCaptureRef.current = true;
    setLock(true);
  }, []);

  const handleEnd = useCallback(() => {
    onCompositionStartCaptureRef.current = false;
    const timer = setTimeout(() => {
      setLock(false);
      clearTimeout(timer);
    }, 200);
  }, []);

  // 防抖
  const handleChange = useMemo(
    () => debounce((data: string) => {
      changed.current = true;
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: {
          text: data,
        },
      });
    }, 300),
    [],
  );

  const handleKeyup = useCallback((_: TextareaValue, ctx: {
    e: React.KeyboardEvent<HTMLTextAreaElement>;
  }) => {
    const { e } = ctx;

    if (e.key === 'Enter') {
      if (lock && (!e.ctrlKey && !e.shiftKey)) {
        changed.current = true;
        dispatch({
          type: reducerActions.UPDATE_ITEM,
          payload: {
            text: `${objectData?.params?.text || ''}`,
          },
        });
        return;
      }


      if (e.ctrlKey || e.shiftKey) {
        dispatch({
          type: reducerActions.UPDATE_ITEM,
          payload: {
            text: `${objectData?.params?.text || ''}\n`,
          },
        });
        return;
      }

      const postData: IEvents = {
        type: EventType.EVENT_TYPE_MESSAGE,
        message: {
          type: ContentType.CONTENT_TYPE_TEXT,
          ...value?.message,
          text: {
            showType: 0,
            text: '',
            dubUrl: {},
          },
          sender: objectData?.params?.sender,
        },
      };
      onEnter(postData);
      textareaRef.current.textareaElement?.blur();
      try {
        setTimeout(() => {
          const elements = document.getElementsByClassName(`events-bot-input-${index + 1}`);
          if (elements?.[0]) {
            elements?.[0]?.querySelector('textarea')?.focus();
          }
        }, 400);
      } catch (error) {
        console.log(error);
      }
    } else {
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: {
          text: `${objectData?.params?.text || ''}`,
        },
      });
    }
  }, [
    onEnter,
    value?.message,
    lock,
    index,
    objectData?.params?.text,
    objectData?.params?.sender?.type,
    objectData?.params?.sender?.senderId,
  ]);

  const handleKeydown = useCallback((_: TextareaValue, ctx: {
    e: React.KeyboardEvent<HTMLTextAreaElement>;
  }) => {
    const e = ctx?.e || {};
    if (e?.code === 'Enter') {
      if (e?.ctrlKey || e?.shiftKey) {
        return;
      }
      if (lock && (!e?.ctrlKey && !e?.shiftKey)) {
        return;
      }
      e?.preventDefault();
    }
  }, []);

  const handleChangeSelect = (data: SelectValue) => {
    if (data !== null && !data) {
      MessagePlugin.error('该角色存在问题，请选择其他角色');
    }
    let senderType = SenderType.BOT;
    if (data === PLAY_INFO_ID) {
      senderType = SenderType.USER;
    } else if (data === SYSTEM_ID) {
      senderType = SenderType.SYSTEM;
    }
    const senderId = data === SYSTEM_ID ? '' : data as string;
    changed.current = true;

    dispatch({
      type: reducerActions.UPDATE_ITEM,
      payload: {
        sender: {
          type: senderType,
          senderId,
        },
      },
    });
  };

  const getOption = () => {
    let options = [];
    if (showSystem) {
      options.push({
        id: SYSTEM_ID,
        name: '旁白',
      });
    }
    if (botList) {
      options.push(...botList);
    }
    if (fieldname === RELATEDMESSAGE) { // 角色发言不可以选择玩家
      options = options.filter(e => e.id !== PLAY_INFO_ID);
    }
    return options.map(e => ({
      label: e?.name,
      value: `${e.id}`,
      title: e?.name,
    }));
  };

  const options = getOption() || [];

  const { botValue, text } = useMemo(() => ({
    botValue: isSystem
      ? SYSTEM_ID
      : value?.message?.sender?.senderId || '',
    text: (value?.message?.text?.text || ''),
  }), [isSystem, value?.message?.sender?.senderId, value?.message?.text?.text]);

  const botAvatar = useMemo(() => {
    if (isSystem) {
      return <SystemIcon style={{ width: 16, height: 16 }} />;
    }
    const bot = botList?.find(e => e.id === botValue);
    if (bot?.url) {
      return <img src={bot.url} alt={bot.name} width={20} height={20} style={{ borderRadius: '50%' }} />;
    }
    return <BotIcon style={{ width: 16, height: 16 }} />;
  }, [isSystem, botValue, botList]);

  const dubUrl = useMemo(() => {
    if (value?.message?.text?.dubUrl?.url) {
      return {
        url: value?.message?.text?.dubUrl,
        ...value?.message?.text?.asyncLoading,
        volume: value?.message?.text?.volume,
      };
    }
    return { url: { url: '' } };
  }, [value?.message?.text?.dubUrl, value?.message?.text?.asyncLoading, value?.message?.text?.volume]);

  const botDel = useMemo(() => {
    if (isSystem) return false;
    if (botList?.length && botValue) {
      return botList?.filter(e => e?.id === botValue)?.length === 0;
    }
    if (botList?.length === 0 && botValue) return true;
    return false;
  }, [botList, botValue]);

  const errorMsg = useMemo(() => {
    if (botDel) {
      return '该角色已删除，请重新选择';
    }
    if (!ignoreBot.includes(fieldname)) {
      if (botValue && !(text)) {
        return `${isSystem ? '旁白' : '对话'}内容不能为空，请填写`;
      }
      if (!botValue && text) {
        return '对话角色不能为空，请选择';
      }
      if (!botValue || !text) {
        return '对话角色不能为空，对话内容不能为空';
      }
    }
    if (!botValue && (text)) {
      return '对话角色不能为空，请选择';
    }
  }, [botDel, botValue, text]);

  const handleFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocus(false);
  }, []);

  const triggedUpload = useCallback(() => {
    if (disabled) {
      return;
    }
    if (openUpload && dubUrl?.url?.url) {
      MessagePlugin.warning('请先删除音频');
      return;
    }
    changed.current = true;

    dispatch({
      type: reducerActions.UPDATE_ITEM,
      payload: {
        openWebDub: !openUpload,
      },
    });
    asyncOnChangeProps({
      showType: 0,
      ...value?.message?.text,
      text: value?.message?.text?.text || '',
      dubUrl: value?.message?.text?.dubUrl,
      asyncLoading: value?.message?.text?.asyncLoading,
      openWebDub: !openUpload,
    });
    setOpenUpload(!openUpload);
  }, [openUpload, dubUrl?.url, disabled, value, changed.current]);

  const handleAudioChange = (audio?: string | RichMediaValue) => {
    changed.current = true;
    const audioValue = audio as RichMediaValue;
    dispatch({
      type: reducerActions.UPDATE_ITEM,
      payload: {
        dubUrl: audioValue?.url,
        openWebDub: !isEmpty(audioValue?.url) || !isEmpty(audioValue?.asyncStatus),
        asyncLoading: { asyncStatus: audioValue?.asyncStatus },
        volume: audioValue?.volume,
      },
    });

    asyncOnChangeProps({
      showType: 0,
      ...value?.message?.text,
      text: value?.message?.text?.text || '',
      dubUrl: audioValue?.url,
      openWebDub: !isEmpty(audioValue?.url) || !isEmpty(audioValue?.asyncStatus),
      asyncLoading: { asyncStatus: audioValue?.asyncStatus },
      volume: audioValue?.volume,
    });
  };

  const asyncOnChangeProps = (params: IEventMessageText) => {
    if (asyncRef.current) {
      const postData: IEvents = {
        type: EventType.EVENT_TYPE_MESSAGE,
        message: {
          type: ContentType.CONTENT_TYPE_TEXT,
          ...value?.message,
          text: {
            ...value?.message?.text,
            ...params,
          },
          sender: value?.message?.sender,
        },
      };
      onChange(postData);
    }
  };

  const commonItemStyle = {
    background: '#313131',
    border: '1px solid F1F1F214',
  };

  const showDubUrl = useMemo(() => {
    if (missionType === MissionType.MISSION_TYPE_PROPS) {
      return false;
    }
    if (ignoreBot.includes(fieldname)) {
      return false;
    }
    return true;
  }, [
    missionType,
    fieldname,
    ignoreBot,
  ]);


  return (
    <div className={classnames(styles.noPadding)}>
      <div className={styles.itemBox}>
        <div
          className={classnames(styles.system, styles.leftType, styles.noPadding, 'draggable')}
        >
          <Select
            clearable={true}
            prefixIcon={botAvatar}
            empty={
              <div className={styles.smallTips}>
                <div>暂无数据</div>
                <div>请先去幕里面关联角色</div>
              </div>
            }
            className={styles.botSelect}
            value={`${botValue}`}
            onChange={handleChangeSelect}
            options={options}
            defaultValue={options?.[0]?.value}
            disabled={disabled}
          />
        </div>
        <div className={classnames(styles.contentBoxBot, { [styles.focus]: focus })}>
          <FormItem
            label=""
            className={styles.item}
            initialData={text}
          >
            <Textarea
              onFocus={handleFocus}
              onBlur={handleBlur}
              ref={textareaRef}
              className={`events-bot-input-${index}`}
              autosize={{ minRows: 1 }}
              disabled={disabled}
              style={{ minHeight: 32 }}
              placeholder="请输入"
              onChange={handleChange}
              onKeyup={handleKeyup}
              onKeydown={handleKeydown}
              onCompositionStartCapture={handleStart}
              onCompositionEndCapture={handleEnd}
            />
            {
              showDubUrl
                ? <div className={styles.botAudio}>
                  <Tooltip
                    content="自定义配音"
                    destroyOnClose
                    showArrow
                    theme="default"
                  >
                    <>
                      {
                        openUpload
                          ? <AddBotLightAudio onClick={triggedUpload} />
                          : <AddBotAudio onClick={triggedUpload} />
                      }
                    </>
                  </Tooltip>
                </div>
                : null
            }
            {
              missionType === MissionType.MISSION_TYPE_PROPS ? null
                : <div className={styles.audition} >
                  <Tooltip
                    content={value?.message?.sender?.type === SenderType.USER ? '玩家不支持AI配音，请体验其他角色AI配音效果～' : '试听AI配音'}
                    destroyOnClose
                    showArrow
                    theme="default"
                  >
                    {loading ? <span className={styles.loadingTTS}><Loading size='12px' /></span> : isPlaying ? <EventPauseDub onClick={handlePause} /> : <EventPlayDub onClick={handlePlay} />}
                    <AudioPlayer url={audioUrl} size={20} playRef={audioRef}
                      getStatus={getStatus}
                      hiddenPlayBtn={true} />
                  </Tooltip>
                </div>
            }
          </FormItem>
        </div>
      </div>
      {
        openUpload || value?.message?.text?.openWebDub
          ? <div className={styles.uploadAudio}>
            <UploadAudio
              onChange={handleAudioChange}
              asyncUploadOnChange={handleAudioChange}
              value={dubUrl}
              disabled={disabled}
              contentEventIdColWidth={(contentEventIdColWidth || 600) - 100}
              itemStyle={[
                {
                  ...commonItemStyle,
                  borderRadius: 0,
                },
                {},
                {
                  ...commonItemStyle,
                  borderRadius: 0,
                },
              ]
              }
              style={{
                borderRadius: '0px 0px 6px 6px',
                background: '#313131',
                border: '1px solid F1F1F214',
              }}
              lineStyle={{
                background: '#262626',
                borderRadius: '0px 0px 6px 6px',
              }}
              showOption={[true, false, true]}
            />
          </div>
          : null
      }
      {errorMsg ? <div className={styles.errors}>{errorMsg}</div> : null}
    </div>
  );
};
export default BotItem;
